import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Skeleton } from "@mui/material";
import IosSwitch from "components/shared/IosSwitch";
import SettingsHeader from "components/shared/SettingsHeader";
import StyledButton from "components/shared/StyledButton";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { DocoonConfigItem, DocoonConfigItemType } from "store/integrationsStore";
import { createDocoonConfigItem, deleteDocoonConfigItem, fetchDocoonConfig } from "store/integrationsStore";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

const SECTIONS: {
    type: DocoonConfigItemType;
    titleKey: string;
    placeholderKey: string;
    addLabelKey: string;
}[] = [
    {
        type: "service",
        titleKey: "DocoonSettings.Services",
        placeholderKey: "DocoonSettings.AddServicePlaceholder",
        addLabelKey: "DocoonSettings.AddService",
    },
    {
        type: "document_type",
        titleKey: "DocoonSettings.DocumentTypes",
        placeholderKey: "DocoonSettings.AddDocumentTypePlaceholder",
        addLabelKey: "DocoonSettings.AddDocumentType",
    },
    {
        type: "template",
        titleKey: "DocoonSettings.Templates",
        placeholderKey: "DocoonSettings.AddTemplatePlaceholder",
        addLabelKey: "DocoonSettings.AddTemplate",
    },
];

const DocoonSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { docoonConfig } = useAppSelector((state) => state.integrations);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        dispatch(fetchDocoonConfig()).finally(() => setIsLoading(false));
    }, [dispatch]);

    const itemsByType = useMemo(() => {
        const grouped: Record<DocoonConfigItemType, DocoonConfigItem[]> = {
            service: [],
            document_type: [],
            template: [],
        };
        for (const item of docoonConfig) grouped[item.type].push(item);
        return grouped;
    }, [docoonConfig]);

    return (
        <div className="mx-auto flex w-full max-w-[65rem] flex-col gap-8 px-12 pb-10 pt-5">
            <SettingsHeader title={t("DocoonSettings.Title")} subtitle={t("DocoonSettings.Subtitle")} />

            <SignSequentialDefaultSection />

            <div className="flex flex-col gap-4">
                <h2 className="m-0 text-sm font-semibold text-[#646464]">{t("DocoonSettings.ListsTitle")}</h2>
                {isLoading ? (
                    <Skeleton variant="rectangular" height={120} />
                ) : (
                    SECTIONS.map(({ type, titleKey, placeholderKey, addLabelKey }) => (
                        <DocoonConfigSection
                            key={type}
                            type={type}
                            title={t(titleKey)}
                            placeholder={t(placeholderKey)}
                            addLabel={t(addLabelKey)}
                            items={itemsByType[type]}
                            onCreate={(value) => dispatch(createDocoonConfigItem({ type, value }))}
                            onDelete={(id) => dispatch(deleteDocoonConfigItem(id))}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

const SignSequentialDefaultSection = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings } = useCompanySettings();
    const persisted = !!getCompanySettings("docoon_sign_sequential_default");
    // Optimistic value so the switch reacts instantly; falls back to the store value once the request settles.
    const [pending, setPending] = useState<boolean | null>(null);

    const handleToggle = (checked: boolean) => {
        setPending(checked);
        dispatch(
            putCompanySettings([
                { settingsName: "docoon_sign_sequential_default", settingsValue: checked ? "1" : "0" },
            ]),
        )
            .unwrap()
            .catch(() => {
                toast.error(`${t("Settings.SomethingWentWrong")}`);
            })
            .finally(() => setPending(null));
    };

    return (
        <div className="flex flex-col gap-4">
            <h2 className="m-0 text-sm font-semibold text-[#646464]">{t("DocoonSettings.DefaultOptionsTitle")}</h2>
            <div className="flex items-center justify-between gap-4 border-0 border-b border-solid border-black/[.12] pb-8 pt-3">
                <div className="flex flex-col gap-0.5 text-[13px] text-[#141516]">
                    <span>{t("DocoonSettings.SignSequentialDefaultLabel")}</span>
                    <span className="text-xs text-[#646464]">
                        {t("DocoonSettings.SignSequentialDefaultDescription")}
                    </span>
                </div>
                <IosSwitch
                    checked={pending ?? persisted}
                    onChange={(_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => handleToggle(checked)}
                />
            </div>
        </div>
    );
};

interface SectionProps {
    type: DocoonConfigItemType;
    title: string;
    placeholder: string;
    addLabel: string;
    items: DocoonConfigItem[];
    onCreate: (value: string) => void;
    onDelete: (id: number) => void;
}

const DocoonConfigSection = ({ type, title, placeholder, addLabel, items, onCreate, onDelete }: SectionProps) => {
    const [t] = useTranslation();
    const [value, setValue] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isAdding) inputRef.current?.focus();
    }, [isAdding]);

    const existingValuesLower = useMemo(() => new Set(items.map((i) => i.value.toLowerCase())), [items]);
    const trimmed = value.trim();
    const isDuplicate = trimmed.length > 0 && existingValuesLower.has(trimmed.toLowerCase());
    const canSubmit = trimmed.length > 0 && !isDuplicate;

    const handleSubmit = () => {
        if (!canSubmit) return;
        onCreate(trimmed);
        setValue("");
        setIsAdding(false);
    };

    const handleCancel = () => {
        setValue("");
        setIsAdding(false);
    };

    return (
        <section className="overflow-hidden rounded-[3px] border border-management-border-grey bg-backgroundZ0">
            <button
                type="button"
                onClick={() => setIsOpen((o) => !o)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-backgroundZ2 focus:outline-none focus-visible:bg-backgroundZ2"
            >
                <div className="flex items-center gap-2.5">
                    <h3 className="text-sm font-semibold text-black">{title}</h3>
                    <span className="rounded-full bg-backgroundZ2 px-2 py-0.5 text-[11px] font-medium text-foreground">
                        {items.length}
                    </span>
                </div>
                <ExpandMoreIcon
                    sx={{ fontSize: 20 }}
                    className={`text-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
            </button>

            {isOpen && (
                <div className="flex flex-col gap-3 border-t border-management-border-grey px-5 py-4">
                    {items.length === 0 && !isAdding ? (
                        <p className="py-2 text-xs italic text-foreground-disabled">{t("DocoonSettings.EmptyList")}</p>
                    ) : (
                        <ul className="flex max-h-64 flex-col divide-y divide-management-border-grey overflow-y-auto rounded-[3px] border border-management-border-grey bg-backgroundZ1">
                            {isAdding && (
                                <li className="flex flex-col gap-1 bg-white px-3 py-1.5">
                                    <div className="flex items-center justify-between gap-2">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={value}
                                            onChange={(e) => setValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleSubmit();
                                                } else if (e.key === "Escape") {
                                                    handleCancel();
                                                }
                                            }}
                                            placeholder={placeholder}
                                            maxLength={200}
                                            name={`docoon-config-${type}`}
                                            aria-invalid={isDuplicate}
                                            className={`h-[1.9rem] flex-1 rounded-[3px] border bg-white px-2 text-sm text-black placeholder:text-foreground-disabled focus:outline-none focus:ring-1 ${
                                                isDuplicate
                                                    ? "border-danger-text focus:border-danger-text focus:ring-danger-text/30"
                                                    : "border-management-border-grey focus:border-[#303030] focus:ring-[#303030]/30"
                                            }`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleSubmit()}
                                            disabled={!canSubmit}
                                            aria-label={t("DocoonSettings.Confirm")}
                                            className="flex shrink-0 items-center justify-center rounded-[3px] p-1 text-success transition-colors hover:bg-[#eafbe7] focus:outline-none focus-visible:ring-1 focus-visible:ring-success disabled:cursor-not-allowed disabled:text-foreground-disabled disabled:hover:bg-transparent"
                                        >
                                            <CheckIcon sx={{ fontSize: 20 }} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            aria-label={t("DocoonSettings.CancelAdd")}
                                            className="flex shrink-0 items-center justify-center rounded-[3px] p-1 text-danger-text transition-colors hover:bg-danger-background focus:outline-none focus-visible:ring-1 focus-visible:ring-danger-text"
                                        >
                                            <CloseIcon sx={{ fontSize: 20 }} />
                                        </button>
                                    </div>
                                    {isDuplicate && (
                                        <p className="text-[11px] text-danger-text">{t("DocoonSettings.Duplicate")}</p>
                                    )}
                                </li>
                            )}
                            {items.map((item) => (
                                <li
                                    key={item.id}
                                    className="group flex items-center justify-between gap-3 px-3 py-2 text-sm text-black transition-colors hover:bg-backgroundZ2"
                                >
                                    <span className="truncate" title={item.value}>
                                        {item.value}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label={t("DocoonSettings.RemoveAriaLabel", { value: item.value })}
                                        onClick={() => onDelete(item.id)}
                                        className="flex shrink-0 items-center justify-center rounded-[3px] p-1 text-foreground transition-colors hover:bg-danger-background hover:text-danger-text focus:outline-none focus-visible:ring-1 focus-visible:ring-danger-text"
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {!isAdding && (
                        <StyledButton
                            buttonType="primary"
                            size="small"
                            onClick={() => setIsAdding(true)}
                            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                            sx={{ "&.MuiButton-root": { width: "fit-content" } }}
                        >
                            {addLabel}
                        </StyledButton>
                    )}
                </div>
            )}
        </section>
    );
};

export default DocoonSettings;
